import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fetchActivityById,
  updateActivity,
  fetchUserById,
} from "../../services/activityService";
import TextEditor from "../../components/TextEditor";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { message } from "antd";
import "./ActivityCard.css";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import { fetchAllDepartments } from "../../services/departmentService";
import { decrypt } from "../../utils/encryption";
import { BASE_URL_IMAGE } from "../../config/api";

const EditActivity = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const decryptedId = decrypt(id);

  const [bannerImage, setBannerImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptOptions, setDeptOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    startDate: "",
    endDate: "",
    conductedBy: "",
    activityMode: "",
    redirectUrl: "",
    description: "",
    status: "Inactive",
  });

  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
          localStorage.removeItem("token");
          setLoggedInUserData(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchAndSetUser();
  }, []);

  useEffect(() => {
    const getDepartments = async () => {
      try {
        const data = await fetchAllDepartments();
        const formatted = data.map((dept) => ({
          value: dept.department_id,
          label: dept.department_name,
        }));
        setDeptOptions([{ value: null, label: "None" }, ...formatted]);
      } catch (error) {
        console.error(error);
      }
    };
    getDepartments();
  }, []);

  useEffect(() => {
    const loadActivityData = async () => {
      try {
        const activity = await fetchActivityById(decryptedId);
        setActivityData(activity.data);
      } catch (error) {
        message.error("Failed to load activity data");
      } finally {
        setIsLoading(false);
      }
    };
    loadActivityData();
  }, [decryptedId]);

  useEffect(() => {
    if (!activityData || deptOptions.length === 0) return;

    setFormData({
      title: activityData.activity_title || "",
      startDate: activityData.activity_start_date
        ? new Date(activityData.activity_start_date).toISOString().split("T")[0]
        : "",
      endDate: activityData.activity_end_date
        ? new Date(activityData.activity_end_date).toISOString().split("T")[0]
        : "",
      conductedBy: activityData.activity_conducted_by || "",
      activityMode: activityData.activity_mode || "",
      redirectUrl: activityData.activity_url || "",
      description: activityData.activity_content || "",
      status: activityData.activity_status || "Inactive",
    });

    const deptOption = deptOptions.find(
      (d) => d.value === activityData.department_id
    );
    if (deptOption) setSelectedDept(deptOption);

    if (activityData.activity_image) {
      setBannerImage(
        `${BASE_URL_IMAGE}/uploads/activityImages/${activityData.activity_image}`
      );
    }
  }, [activityData, deptOptions]);

  const toggleStatus = () => {
    setFormData((prev) => ({
      ...prev,
      status: prev.status === "Active" ? "Inactive" : "Active",
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerImage(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Determine department ID based on user role
    let dept_id = "";
    if (loggedInUserData?.user_role === "2") {
      // Department Co-ordinator - use their department ID
      dept_id = loggedInUserData.department_id;
    } else {
      // Admin - use selected department or existing department ID
      dept_id = selectedDept?.value || activityData?.department_id || null;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("content", formData.description);
    formDataToSend.append("status", formData.status);
    formDataToSend.append("startDate", formData.startDate);
    formDataToSend.append("endDate", formData.endDate);
    formDataToSend.append("conductedBy", formData.conductedBy);
    formDataToSend.append("mode", formData.activityMode);
    formDataToSend.append("url", formData.redirectUrl);
    formDataToSend.append("department", dept_id);

    // Add user email and parameter for backend logging
    if (loggedInUserData?.user_email) {
      formDataToSend.append("updatedbyemail", loggedInUserData.user_email);
      const parameter =
        loggedInUserData?.user_role === "1" ? "Admin" : "Department";
      formDataToSend.append("parameter", parameter);
    }

    if (bannerImage) {
      formDataToSend.append("activity_image", bannerImage);
    }

    try {
      await updateActivity(decryptedId, formDataToSend);
      message.success("Activity updated successfully!");
      navigate("/list-activities");
    } catch (error) {
      message.error(error.message || "Failed to update Activity");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center p-5">Loading activity data...</div>;
  }

  return (
    <div className="main-wrapper">
      <Header />
      <Sidebar
        id="menu-item-activity"
        id1="menu-items-activities"
        activeClassName="edit-blog"
      />

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Activity</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FeatherIcon icon="chevron-right" />
                  </li>
                  <li className="breadcrumb-item active">Edit Activity</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Activity Details</h4>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Activity Title{" "}
                            <span className="login-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Select Start Date{" "}
                            <span className="login-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Select End Date{" "}
                            <span className="login-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Conducted By <span className="login-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            name="conductedBy"
                            value={formData.conductedBy}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>
                            Select Activity Mode{" "}
                            <span className="login-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            name="activityMode"
                            value={formData.activityMode}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Mode</option>
                            <option value="Online">Online</option>
                            <option value="Offline">Offline</option>
                            <option value="Hybrid">Hybrid</option>
                          </select>
                        </div>
                      </div>

                      {loggedInUserData?.user_role !== "2" ? (
                        <div className="col-12">
                          <div className="form-group local-forms">
                            <label>
                              Select Department (Type to search and select)
                              <span className="login-danger">*</span>
                            </label>
                            <Select
                              value={selectedDept}
                              onChange={setSelectedDept}
                              options={deptOptions}
                              components={{
                                IndicatorSeparator: () => null,
                              }}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderColor: state.isFocused
                                    ? "#2e37a4"
                                    : "rgba(46, 55, 164, 0.1)",
                                  boxShadow: "none",
                                  borderRadius: "10px",
                                  fontSize: "14px",
                                  minHeight: "45px",
                                }),
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="col-12">
                          <div className="form-group local-forms">
                            <label>Department</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                deptOptions.find(
                                  (d) =>
                                    d.value === loggedInUserData.department_id
                                )?.label || "Department not found"
                              }
                              disabled
                            />
                          </div>
                        </div>
                      )}

                      <div className="col-md-6">
                        <div className="form-group local-forms">
                          <label>Redirect URL</label>
                          <input
                            className="form-control"
                            name="redirectUrl"
                            value={formData.redirectUrl}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="form-group local-forms">
                          <label className="form-label d-block mb-2">
                            Status: <strong>{formData.status}</strong>
                          </label>
                          <label className="switch mt-4">
                            <input
                              type="checkbox"
                              checked={formData.status === "Active"}
                              onChange={toggleStatus}
                            />
                            <span className="slider round"></span>
                          </label>
                        </div>
                        <small className="d-block text-muted mt-5">
                          Enable this to make the activity Active.
                        </small>
                      </div>

                      <div className="col-12 mt-3">
                        <TextEditor
                          value={formData.description}
                          onChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              description: value,
                            }))
                          }
                        />
                      </div>

                      <div className="col-md-6 mt-3">
                        <div className="form-group local-forms">
                          <label>Current Activity Image</label>
                          {bannerImage && (
                            <img
                              src={bannerImage}
                              alt="Current Banner"
                              className="img-thumbnail mb-2"
                              style={{ maxHeight: "150px" }}
                            />
                          )}
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleBannerChange}
                            accept="image/*"
                          />
                        </div>
                      </div>

                      <div className="col-12 mt-4 text-end">
                        <button
                          type="submit"
                          className="btn btn-primary me-2"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Updating..." : "Update Activity"}
                        </button>
                        <Link
                          to="/list-activities"
                          className="btn btn-secondary"
                        >
                          Cancel
                        </Link>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditActivity;
